import { useFormContext, useWatch } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { debounce } from "lodash";

interface IRequiredFields {
  initialPathName: string;
  getRequiredFields: (formValues: any) => string[];
}

/**
 *
 * Hook to show the required fields in the form with a red asterisk
 *  @param {IRequiredFields} props funtions to get the required fields and the initial path name
 *  initialPathName: string;
 *  pathName string; to remove the style when the path change
 * @returns
 */
const useRequiredFields = (props: IRequiredFields) => {
  const { pathname } = useLocation();
  const { control } = useFormContext();
  const formValuesState = useWatch({ control });

  const applyRequiredStyles = useCallback(
    (formValues: any) => {
      const required = props.getRequiredFields(formValues) || [];

      const styleId = "required-fields-style";
      const oldStyle = document.getElementById(styleId);
      if (oldStyle) document.head.removeChild(oldStyle);

      if (!required.length) return; // Nothing to mark

      const css = required
        .map(fieldName => {
          return `[data-field-name="${fieldName}"] .MuiFormLabel-root::after { content: " *"; color: red; }`;
        })
        .join("\n");

      const styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.innerHTML = css;
      document.head.appendChild(styleElement);
    },
    [props]
  );

  const debouncedApply = useMemo(
    () => debounce(applyRequiredStyles, 500),
    [applyRequiredStyles]
  );

  useEffect(() => {
    debouncedApply(formValuesState);
    return () => debouncedApply.cancel();
  }, [formValuesState, debouncedApply]);

  // Cleanup styles when navigating away from the initial path
  useEffect(() => {
    if (props.initialPathName !== pathname) {
      const el = document.getElementById("required-fields-style");
      if (el) document.head.removeChild(el);
    }
  }, [pathname, props.initialPathName]);

  return null;
};

export default useRequiredFields;
